import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

import { getLeads } from '@/services/api/meta-leads';
import { testSendBuilderItem } from '@/services/api/template-builder';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: number;
  pageId: string;
  onSuccess?: () => void;
}

export default function TestSendDialog({ open, onOpenChange, itemId, pageId, onSuccess }: Props) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [leadId, setLeadId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['template-builder', 'leads', pageId, q],
    queryFn: () => getLeads({ page_id: pageId, per_page: 20, search: q || undefined, is_active: true }),
    enabled: open && !!pageId,
  });
  const leads = (data as any)?.items ?? [];

  const send = useMutation({
    mutationFn: () => testSendBuilderItem(itemId, leadId as number),
    onSuccess: (r) => {
      if (r.ok) { toast({ title: 'Enviado ao lead — confira no Messenger' }); onSuccess?.(); onOpenChange(false); }
      else toast({ title: 'Falha no envio', description: String(r.detail ?? ''), variant: 'destructive' });
    },
    onError: (e: any) => toast({ title: 'Erro', description: String(e?.message ?? e), variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Envio de teste</DialogTitle></DialogHeader>
        <p className="-mt-2 text-xs text-muted-foreground">Leads da página · entrega REAL no Messenger</p>
        <Input placeholder="Buscar lead por nome ou PSID…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-[52vh] space-y-1 overflow-y-auto">
          {isLoading && <div className="py-6 text-center text-sm text-muted-foreground">Carregando…</div>}
          {!isLoading && leads.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Nenhum lead encontrado.</div>}
          {leads.map((l: any) => {
            const open24 = l.in_24h_window === true || l.in_24h_window === 1;
            const selected = leadId === l.id;
            return (
              <button
                key={l.id}
                onClick={() => setLeadId(l.id)}
                className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition ${selected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'hover:bg-muted'}`}
              >
                <span className={`h-3.5 w-3.5 shrink-0 rounded-full border ${selected ? 'border-4 border-indigo-600' : 'border-slate-300'}`} />
                <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-slate-300 to-slate-400" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{l.nome || 'Lead'} {l.sobrenome ?? ''}</div>
                  <div className="truncate text-xs text-muted-foreground">{l.sender_id}</div>
                </div>
                <span className={`shrink-0 text-xs font-medium ${open24 ? 'text-emerald-600' : 'text-slate-400'}`}>{open24 ? 'janela aberta' : 'fora da janela'}</span>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!leadId || send.isPending} onClick={() => send.mutate()}>{send.isPending ? 'Enviando…' : 'Disparar teste'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
